import React, { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BackButton } from '@/components/BackButton/BackButton'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Spinner } from 'components/Spinner'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'

import { FeatureFlag } from '../FeatureFlags'
import { FeatureFlagForm, FeatureFlagFormSubmitValues } from './FeatureFlagForm'

export interface CreateFeatureFlag extends Omit<FeatureFlag, 'id' | 'versions'> {
  versions: {
    versionId: string
    isEnabled: boolean
  }[]
}

export const FeatureFlagDetailsPage = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const { id } = useParams<{ id: string }>()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const socialMediaTypes = useAppSelector((state) => state.wallet.socialMediaTypes)

  const { callback: fetchFeatureFlag, forceRefresh } = useCallbackWithForceRefresh(
    () => (id && id !== 'new' ? clientAreaApiClient.getFeatureFlagById(id) : Promise.resolve(null)),
    [id]
  )

  const { data, isLoading } = useFetchOne(fetchFeatureFlag)

  const setToast = useContext(ToastContext)
  const navigate = useNavigate()

  const onSubmit = async (featureFlag: FeatureFlagFormSubmitValues) => {
    try {
      if (featureFlag.id) {
        await clientAreaApiClient.updateFeatureFlag(featureFlag as FeatureFlag)
        setToast(successToast('Feature Flag updated successfully'))
      } else {
        const newFeatureFlag: CreateFeatureFlag = {
          ...featureFlag,
          versions: featureFlag.versions.map((version) => ({
            versionId: version.version?.id ?? '',
            isEnabled: version.isEnabled ?? false,
          })),
        }
        await clientAreaApiClient.addFeatureFlag(newFeatureFlag)
        navigate(links.productFeatureFlags.url)
        setToast(successToast('Feature Flag added successfully'))
      }
    } catch (_) {
      setToast(errorToast('Error updating Feature Flag'))
    } finally {
      forceRefresh()
    }
  }

  if (isLoading) return <Spinner />

  return (
    <React.Fragment>
      <div>
        <Breadcrumbs
          crumbs={[
            {
              title: `Feature Flag - ${data?.name ?? 'New'}`,
              link: links.recordsPaymentAgents.url,
            },
          ]}
        />
        <BackButton onClick={() => navigate(links.productFeatureFlags.url)} />
        <FeatureFlagForm
          featureFlag={data}
          permissions={permissions}
          onSubmit={onSubmit}
          forceRefresh={forceRefresh}
          socialMediaTypes={socialMediaTypes}
        />
      </div>
    </React.Fragment>
  )
}
